'use client';

import dynamic from 'next/dynamic';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import logWebHeaderHovered from '@alltrails/amplitude/events/logWebHeaderHovered';
import { useLanguageRegionCode } from '@alltrails/core/hooks/useLanguageRegionCode';
import getFullName from '@alltrails/core/utils/getFullName';
import Avatar from '@alltrails/denali/components/Avatar';
import AvatarGroup from '@alltrails/denali/components/AvatarGroup';
import DropdownMenu from '@alltrails/denali/components/DropdownMenu';
import useExperiments from '@alltrails/experiments/hooks/useExperiments';
import useExposureEvent from '@alltrails/experiments/hooks/useExposureEvent';
import InstallModal from '@alltrails/install-prompt/components/InstallModal';
import type Context from '@alltrails/types/Context';
import type { User } from '@alltrails/types/User';
import useUpsellContent from '@alltrails/upsell/hooks/useUpsellContent';
import GetTheAppButton from './GetTheAppButton';
import { getUserDropdownOptions } from './headerLinks';
import NavCreateRouteButton from './NavCreateRouteButton';
import StartFreeTrialButton from './StartFreeTrialButton';
import styles from './styles/styles.module.scss';
import { HeaderBackground } from './types';

const GenericPlusUpsell = dynamic(() => import('@alltrails/upsell/components/UpsellSystem/GenericPlusUpsell/GenericPlusUpsell'), { ssr: false });

type LoggedInUserCtaProps = {
  background: HeaderBackground;
  user: User;
};

const LoggedInUserCta = ({ background, user }: LoggedInUserCtaProps) => {
  const languageRegionCode = useLanguageRegionCode();
  const adminImpersonator = useSelector((state: { context: Context }) => state.context.impersonator);
  const experiments = useExperiments();

  const isGlobalNavCtaEligible = Boolean(user.isPromoEligible);
  const navCreateXPValue = experiments['web-disco-rm-nav-create']?.value ?? 'control';
  const isInverted = background === 'transparent-inverted';

  const [isOpen, setIsOpen] = useState(false);
  const [showInstallModal, setShowInstallModal] = useState(false);
  const { isOpen: isUpsellOpen, serverUpsellContent, setIsOpen: setIsUpsellOpen } = useUpsellContent('Global Nav Menu');

  const sendUpsellExposure = useExposureEvent(serverUpsellContent?.experimentKey);

  // Sent here rather than by the button, so the exposure reflects the copy the modal actually opened with.
  useEffect(() => {
    if (isUpsellOpen && serverUpsellContent?.experimentKey) {
      sendUpsellExposure();
    }
  }, [isUpsellOpen, sendUpsellExposure, serverUpsellContent?.experimentKey]);

  const openDropdown = () => {
    logWebHeaderHovered({ web_header_position: navCreateXPValue === 'dropdown' ? 4 : 3, web_header_section: 'my account' });
    setIsOpen(true);
  };

  // Wrapping div around the Avatar is needed for the DropdownMenu to set a ref
  const anchor = (
    <div>
      {adminImpersonator ? (
        <AvatarGroup
          avatars={[
            { onClick: openDropdown, userId: user.id, userName: getFullName(user) },
            { userId: adminImpersonator.id, userName: adminImpersonator.name }
          ]}
        />
      ) : (
        <Avatar onClick={openDropdown} size="sm" userId={user.id} userName={getFullName(user)} />
      )}
    </div>
  );

  const options = getUserDropdownOptions({
    user,
    languageRegionCode,
    isAdminImpersonating: Boolean(adminImpersonator),
    onContinueInApp: () => {
      setIsOpen(false);
      setShowInstallModal(true);
    }
  });

  return (
    <>
      <NavCreateRouteButton background={background} />
      {isGlobalNavCtaEligible ? (
        <>
          <GetTheAppButton {...(isInverted && { variant: 'inverse' })} />
          <StartFreeTrialButton onClick={() => setIsUpsellOpen(true)} variant="accent" />
        </>
      ) : (
        <GetTheAppButton variant="accent" />
      )}
      <DropdownMenu
        anchor={anchor}
        placement="bottom-end"
        className={styles.dropdownMenu}
        containerMouseHandlers={{ onMouseEnter: openDropdown, onMouseLeave: () => setIsOpen(false) }}
        isOpen={isOpen}
        onCloseRequest={() => setIsOpen(false)}
        options={options}
      />
      <InstallModal isOpen={showInstallModal} onCloseRequest={() => setShowInstallModal(false)} location="global nav profile dropdown" />
      {isUpsellOpen && (
        <GenericPlusUpsell
          isOpen={isUpsellOpen}
          onCloseRequest={() => setIsUpsellOpen(false)}
          serverUpsellContent={serverUpsellContent}
          upsellTrigger="Global Nav Menu"
        />
      )}
    </>
  );
};

export default LoggedInUserCta;
